import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import breakpoints from "../../../styles/breakpoints"
import fonts from "../../../styles/fonts"
import colors from "../../../styles/colors"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import Slider from "react-slick"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { getSignupFormId } from "../../../services/klaviyo"
import { getCurrentVersion } from "../../../services/environmentHelper"
import { isBrowser } from "../../../services/browser"
import BlockContainer from "../BlockContainer"
import LocationPopup from "../LocationPopup"
import { useBannerContext } from "../../../context/BannerContext"

const DESKTOP_FORM_ID = getSignupFormId()
const MOBILE_FORM_ID = getSignupFormId("mobile")

const PromoContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  background: #B8B889;
  align-items: center;
  justify-content: center;
  height: ${(props) => (props.visible ? "auto" : "0")};
  overflow: hidden;
  position: relative;

  @media (min-width: ${breakpoints.lg}) {
    background: ${colors.secondaryBeigeLight};
    height: auto;
  }
  /* @media (min-width: ${breakpoints.xl}) {
    padding: 10px 92px;
  } */
`

const Text = styled.div`
  ${fonts.ivyStyleLight};
  font-size: 12px;
  color: ${colors.trueBlack};
  outline: none;
  text-align: center;
  line-height: 1.4;
  height: ${(props) => (props.height ? `${props.height}px` : "auto")};
  display: flex !important;
  justify-content: center;
  align-items: center;
  padding: 4px 0;
  width: 100%;
  b {
    ${fonts.helveticaNeueMedium}
  }
  a {
    color: inherit;
    &:hover {
      text-decoration: none;
    }
  }

  i {
    font-style: italic;
  }
  #klaviyo_form_trigger {
    display: inline;
    text-decoration: underline;
    cursor: pointer;
  }

  @media (min-width: ${breakpoints.md}) {
    text-align: left;
    padding: 4px 120px 4px 0;
    justify-content: flex-start;
  }
`

const StyledSlider = styled(Slider)``

const Location = styled.button`
  ${fonts.ivyStyleLight};
  padding: 0;
  background: transparent;
  border: none;
  font-size: 10px;
  line-height: 1.4;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0;
  display: none;
  font-style: italic;
  text-align: right;
  color: ${colors.trueBlack};

  &:hover {
    text-decoration: underline;
  }

  @media (min-width: ${breakpoints.md}) {
    display: block;
  }
`

const PromoBanner = (props) => {
  const { banners, visible, alternatePath } = props
  let promotionSlides = banners?.[0]?.slides
  const bannerRef = useRef(null)
  const [slideHeight, setSlideHeight] = useState(0)
  const [popupOpen, setPopupOpen] = useState(false)
  const { setBannerHeight} = useBannerContext()

  const handlePopup = (value) => {
    setPopupOpen(value)
  }

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    fade: false,
    swipe: true,
    pauseOnHover: false,
    draggable: false,
    speed: 800,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    autoplaySpeed: 4000,
    vertical: true,
    centerMode: false,
    adaptiveHeight: false,
  }

  const currentVersion = getCurrentVersion()

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
      [INLINES.HYPERLINK]: (node, children) => {
        return node.data.uri.includes("signup") ? (
          <span id="klaviyo_form_trigger">{children}</span>
        ) : (
          <a href={node.data.uri}>{children}</a>
        )
      },
    },
    renderText: (text) =>
      text && typeof text !== "undefined"
        ? text.replace("$", currentVersion.currencySign)
        : "",
  }

  useEffect(() => {
    if (bannerRef.current) {
      setSlideHeight(bannerRef.current?.clientHeight)
    }
  }, [])

  useEffect(() => {
    if (isBrowser()) {
      const formTrigger = document.getElementById("klaviyo_form_trigger")
      const formId = window.innerWidth >= 600 ? DESKTOP_FORM_ID : MOBILE_FORM_ID

      if (formTrigger) {
        formTrigger.addEventListener("click", function () {
          const _klOnsite = window._klOnsite || []
          _klOnsite.push(["openForm", formId])
        })
      }
    }
  })

  useEffect(() => {
    const handleResize = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.clientHeight) //2 for the border height
      }
    }
    handleResize()
    window.addEventListener("scroll", handleResize)
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("scroll", handleResize)
      window.removeEventListener("resize", handleResize)
    }
  }, [setBannerHeight])

  return (
    <PromoContainer
      ref={(el) => (bannerRef.current = el)}
      id="promo-banner"
      visible={visible ? 1 : 0}
    >
      <BlockContainer style={{ position: "relative" }} header={true}>
        {promotionSlides.length > 1 ? (
          <StyledSlider {...settings}>
            {promotionSlides.map((slide, index) => (
              <Text key={index} height={slideHeight}>
                <div>{renderRichText(slide.bannerText, options)}</div>
              </Text>
            ))}
          </StyledSlider>
        ) : (
          <Text>{renderRichText(promotionSlides[0].bannerText, options)}</Text>
        )}
        <Location onClick={() => handlePopup(true)}>
          {currentVersion.name} ({currentVersion.currency})
        </Location>
      </BlockContainer>
      <LocationPopup
        handlePopup={handlePopup}
        isOpen={popupOpen}
        alternatePath={alternatePath}
      />
    </PromoContainer>
  )
}

export default PromoBanner
